import { useQueryClient } from '@tanstack/react-query';

import { useAppDispatch } from '@pkg/hooks';
import { LocalStorageKeys } from '@pkg/types';
import { clearLocalStorage } from '@pkg/utils';

import { apiFactory } from '@app/client';
import { APP_ANONYMOUS_LANDING_PAGE } from '@app/routes/paths';
import { resetReduxStore } from '@app/store/actions/user';
import { resetAllStores } from '@app/store/zustand/helpers';

export function useLogout() {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();

    function disconnect() {
        apiFactory().disconnectWebsocket();
    }

    function flushStores() {
        // Cleanup Redux and Zustand stores
        dispatch(resetReduxStore());
        resetAllStores();

        // Invalidate all React Query cache
        void queryClient.invalidateQueries();
    }

    function sanitizeSession() {
        // Disconnect from all open websocket connections
        disconnect();

        // Flush all stores (Redux, Zustand, React Query)
        flushStores();

        // Clear browser's local storage
        clearLocalStorage({
            ignoreKeys: [LocalStorageKeys.AuthRedirectType, LocalStorageKeys.AuthRedirectPath],
        });
    }

    function logout({ redirectPath }: { redirectPath?: string | null } = {}) {
        sanitizeSession();

        /*
         * Force a hard redirect in order to clear any data that might still be
         * in the browser's memory. This is important to avoid any potential
         * glitches that might occur.
         */
        if (redirectPath) {
            window.location.href = redirectPath;
        } else {
            window.location.href = APP_ANONYMOUS_LANDING_PAGE;
        }
    }

    return {
        logout,
        sanitizeSession,
    };
}
